<template>
  <div class="progress-circle-div" v-tooltip="isNone ? $t('app.campaign.results.noCompatibility') : undefined">
    <svg
      :width="size"
      :height="size"
      :viewBox="`0 0 ${size} ${size}`"
      class="progress-circle"
    >
      <!-- Background Circle -->
      <circle
        class="progress-bg"
        :r="radius"
        :cx="size / 2"
        :cy="size / 2"
        :stroke-width="stroke"
        fill="none"
        :stroke-dasharray="dashArray"
        :stroke-dashoffset="0"
        :transform="rotation"
      />
  
      <!-- Progress Circle -->
      <circle
        class="progress-bar"
        :r="radius"
        :cx="size / 2"
        :cy="size / 2"
        :stroke-width="stroke"
        :stroke="progressBarColor"
        fill="none"
        :stroke-dasharray="dashArrayArr"
        :stroke-dashoffset="0"
        :transform="rotation"
      />
    </svg>
    <div class="value" :style="{ fontSize, color: progressBarColor }">{{ props.isNone ? '-' : Math.round(props.percentage) }}</div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { toNumber } from '@vue/shared'

const props = defineProps({
  percentage: {
    type: Number,
    required: true,
  },
  size: {
    type: Number,
    default: 120
  },
  stroke: {
    type: Number,
    default: 10
  },
  gapAngle: {
    type: Number,
    default: 90 // Opening at the bottom (360 - 270 = 90° gap)
  },
  isNone: {
    type: Boolean,
    default: false
  }
});

const state = reactive({
  colors: [
    "#822312",
    "#822312",
    "#CD5500",
    "#CD5500",
    "#EDAE39",
    "#EDAE39",
    "#92C93A",
    "#92C93A",
    "#00AF00",
    "#00AF00",
  ],
  rank_colors: {
    A: "#00AF00",
    B: "#92C93A",
    C: "#EDAE39",
    D: "#CD5500",
    E: "#822312",
  },
});

const fontSize = computed(() => `${(props.size - props.stroke) * 0.42}px`);

const progressBarColor = computed(() => state.colors[Math.floor((props.percentage) / state.colors.length)] || "#00AF00");

const radius = computed(() => (props.size - props.stroke) / 2)
const circumference = computed(() => 2 * Math.PI * radius.value)

const gapLength = computed(() => (props.gapAngle / 360) * circumference.value)
const dashArray = computed(() => circumference.value - gapLength.value)

const dashArrayArr = computed(() => {
  const baseDashArray = dashArray.value;
  const visibleDashArray = baseDashArray * (props.percentage / 100);
  const gap = circumference.value - visibleDashArray;
  return `${visibleDashArray} ${gap}`;
})

const rotation = computed(() => {
  const offsetAngle = props.gapAngle / 2
  return `rotate(${90 + offsetAngle} ${props.size / 2} ${props.size / 2})`
})
</script>

<style scoped lang="scss">
@import "@/assets/style/variables.scss";

.progress-circle-div {
  position: relative;
  width: min-content;
  .value {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    line-height: 100%;
    text-align: center;
    font-size: 14px;
    font-family: $bioSans;
    font-weight: 700;
  }
}

.progress-circle {
  display: block;
  transform: rotate(0deg);
}
.progress-bg {
  stroke: var(--light-grey-color);
  stroke-linecap: round;
}
.progress-bar {
  transition: stroke-dashoffset 0.6s ease;
  stroke-linecap: round;
}
</style>
